import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import SearchInput from '../components/ui/SearchInput';
import Badge from '../components/ui/Badge';
import { formatDate } from '../utils/helpers';
import { Filter, Search, FileText, Inbox, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSettings } from '../context/SettingsContext';
import { useUIStore } from '../store/useUIStore';

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ dispatches: [], receipts: [], efiles: [] });
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { get } = useApi();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { tableDensity, setTableDensity } = useUIStore();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    if (query.trim().length < 3) {
      toast.error('Search query must be at least 3 characters');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await get(`/search?q=${query}`);
      // Group results by type
      const data = response.data;
      setResults({
        dispatches: data.filter(item => item.type === 'dispatch'),
        receipts: data.filter(item => item.type === 'receipt'),
        efiles: isEfileEnabled ? data.filter(item => item.type === 'efile') : []
      });
    } catch (error) {
      console.error('Search failed', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setHasSearched(false);
    setResults({ dispatches: [], receipts: [], efiles: [] });
  };

  const dispatchColumns = [
    { 
      header: 'Dispatch No.', 
      accessorKey: 'number',
      render: (row) => (
        <span className="font-medium text-primary-600">{row.number}</span>
      )
    },
    { header: 'Subject', accessorKey: 'subject', className: 'max-w-xs truncate' },
    { header: 'Date', accessorKey: 'date', render: (row) => formatDate(row.date) },
    { header: 'Sent To', accessorKey: 'recipient', render: (row) => row.recipient || '-' },
    { 
      header: 'Action', 
      accessorKey: 'id', 
      render: (row) => <Button size="sm" variant="ghost" onClick={() => navigate(`/dispatch/${row.id}/view`)}>View</Button> 
    }
  ];

  const receiptColumns = [
    { 
      header: 'Receipt No.', 
      accessorKey: 'number',
      render: (row) => (
        <span className="font-medium text-primary-600">{row.number}</span>
      )
    },
    { header: 'Subject', accessorKey: 'subject', className: 'max-w-xs truncate' },
    { header: 'Date', accessorKey: 'date', render: (row) => formatDate(row.date) },
    { header: 'Received From', accessorKey: 'sender', render: (row) => row.sender || '-' },
    { header: 'Assign To', accessorKey: 'assignedTo', render: (row) => row.assignedTo || '-' },
    { 
      header: 'Action', 
      accessorKey: 'id', 
      render: (row) => <Button size="sm" variant="ghost" onClick={() => navigate(`/diary/${row.id}/view`)}>View</Button> 
    }
  ];

  const efileColumns = [
    { 
      header: 'File No.', 
      accessorKey: 'number',
      render: (row) => (
        <span className="font-medium text-primary-600">{row.number}</span>
      )
    },
    { header: 'Subject', accessorKey: 'subject', className: 'max-w-xs truncate' },
    { header: 'Status', accessorKey: 'status', render: (row) => <Badge>{row.status}</Badge> },
    { 
      header: 'Action', 
      accessorKey: 'id', 
      render: (row) => <Button size="sm" variant="ghost" onClick={() => navigate(`/efile/${row.id}`)}>Open</Button> 
    }
  ];

  return (
    <>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Global Search</h1>
          <p className="text-gray-500 max-w-lg mx-auto">
            {isEfileEnabled
              ? 'Search across dispatches, diaries, and e-files by number, metadata, keywords, and OCR text.'
              : 'Search across dispatches and diaries by number, metadata, keywords, and OCR text.'}
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 mb-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-primary-50 rounded-full blur-xl"></div>
          <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-primary-50 rounded-full blur-xl"></div>
          
          <form onSubmit={handleSearch} className="relative z-10 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput 
                value={query} 
                onChange={setQuery} 
                onClear={clearSearch}
                placeholder="Enter keywords, reference numbers (e.g., DISP/...), or subjects..." 
                className="w-full"
              />
            </div>
            <Button type="submit" isLoading={loading} size="lg" icon={Search}>
              Search Records
            </Button>
          </form>
        </div>

        {hasSearched && (
          <div className="space-y-8 animate-fade-in">
            <div className="flex justify-end">
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                <Button
                  size="sm"
                  variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
                  onClick={() => setTableDensity('comfortable')}
                >
                  Comfortable
                </Button>
                <Button
                  size="sm"
                  variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
                  onClick={() => setTableDensity('compact')}
                >
                  Compact
                </Button>
              </div>
            </div>
            {results.dispatches.length === 0 && results.receipts.length === 0 && (!isEfileEnabled || results.efiles.length === 0) ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your search terms or filters.
                </p>
              </div>
            ) : (
              <>
                {results.dispatches.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
                    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
                      <Send className="w-5 h-5 text-indigo-500" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">Dispatches</h3>
                        <p className="text-sm text-gray-500">{results.dispatches.length} record{results.dispatches.length !== 1 ? 's' : ''} found</p>
                      </div>
                    </div>
                    <div className="p-0">
                      <Table 
                        columns={dispatchColumns} 
                        data={results.dispatches} 
                        className="!border-0 !shadow-none !rounded-none"
                        density={tableDensity}
                      />
                    </div>
                  </div>
                )}

                {results.receipts.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
                      <Inbox className="w-5 h-5 text-green-500" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">Receipts</h3>
                        <p className="text-sm text-gray-500">{results.receipts.length} record{results.receipts.length !== 1 ? 's' : ''} found</p>
                      </div>
                    </div>
                    <div className="p-0">
                      <Table 
                        columns={receiptColumns} 
                        data={results.receipts} 
                        className="!border-0 !shadow-none !rounded-none"
                        density={tableDensity}
                      />
                    </div>
                  </div>
                )}

                {isEfileEnabled && results.efiles.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">e-Files</h3>
                        <p className="text-sm text-gray-500">{results.efiles.length} record{results.efiles.length !== 1 ? 's' : ''} found</p>
                      </div>
                    </div>
                    <div className="p-0">
                      <Table 
                        columns={efileColumns} 
                        data={results.efiles} 
                        className="!border-0 !shadow-none !rounded-none"
                        density={tableDensity}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchPage;
